import { LitElement, html, TemplateResult, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { burgerMenuIcon, closeIcon } from "@/assets/icons";
import {
  USER_ACCOUNT_SETTINGS_MODE,
  USER_ACCOUNT_SETTINGS_ORIENTATION,
  USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE,
  USER_ACCOUNT_SETTINGS_TAB_LIST,
} from "@/types";
import {
  BURGER_BUTTON_CLASSES,
  BURGER_LABEL_CLASSES,
  MOBILE_DRAWER_CLASSES,
  MOBILE_OVERLAY_CLASSES,
  SIDEBAR_CONTAINER_CLASSES,
} from "./classes";
import { keyed } from "lit/directives/keyed.js";
import { cn } from "@/utils";

export interface SidebarConfig {
  showIcons: boolean;
  modalIcon?: TemplateResult;
}

@customElement("sb-profile-sidebar")
export class SbProfileSidebar extends LitElement {
  @property({ type: String })
  orientation: USER_ACCOUNT_SETTINGS_ORIENTATION =
    USER_ACCOUNT_SETTINGS_ORIENTATION.HORIZONTAL;

  @property({ type: String })
  displayType: USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE =
    USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE.REGULAR;

  @property({ type: String })
  mode: USER_ACCOUNT_SETTINGS_MODE = USER_ACCOUNT_SETTINGS_MODE.PAGE;

  @property({ type: Object })
  config: SidebarConfig = { showIcons: true };

  @property({ type: String })
  activeTab: USER_ACCOUNT_SETTINGS_TAB_LIST =
    USER_ACCOUNT_SETTINGS_TAB_LIST.PROFILE;

  @state()
  private isMenuOpen = false;

  get isBurger() {
    return (
      this.displayType === USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE.BURGER
    );
  }

  private get isVertical() {
    return this.orientation === USER_ACCOUNT_SETTINGS_ORIENTATION.VERTICAL;
  }

  private get isModal() {
    return this.mode === USER_ACCOUNT_SETTINGS_MODE.MODAL;
  }

  private get sidebarKey() {
    return `${this.orientation}|${this.displayType}`;
  }

  createRenderRoot() {
    return this;
  }

  private toggleMenu() {
    const parentTabDiv = this.closest('div[role="tab"]');
    if (parentTabDiv) {
      parentTabDiv.classList.toggle("sb-rotate-0");
    }
    this.isMenuOpen = !this.isMenuOpen;
    this.requestUpdate();
  }

  private renderTabs(isVertical: boolean) {
    return keyed(
      this.sidebarKey,
      html`
        <rtg-tabs-list
          data-burger=${this.isBurger}
          data-orientation=${isVertical ? "vertical" : "horizontal"}
        >
          <rtg-tabs-trigger value=${USER_ACCOUNT_SETTINGS_TAB_LIST.PROFILE}>
            ${USER_ACCOUNT_SETTINGS_TAB_LIST.PROFILE}
          </rtg-tabs-trigger>
          <rtg-tabs-trigger value=${USER_ACCOUNT_SETTINGS_TAB_LIST.SECURITY}>
            ${USER_ACCOUNT_SETTINGS_TAB_LIST.SECURITY}
          </rtg-tabs-trigger>
        </rtg-tabs-list>
      `,
    );
  }

  private renderWatermark() {
    return html`<sb-watermark custom-id="sb-watermark"></sb-watermark>`;
  }

  private renderBurger() {
    return html`
      <div class="sb-flex sb-justify-between sb-w-full">
        <div class="sb-flex sb-items-center sb-gap-2">
          <div class="${BURGER_BUTTON_CLASSES}" @click=${this.toggleMenu}>
            ${burgerMenuIcon()}
          </div>
          <rtg-typography-h part="burger-title-label" level="h3"
            >Account Settings</rtg-typography-h
          >
        </div>
        <div class="sb-flex sb-items-center">
          <rtg-button
            variant="link"
            size="icon-xs"
            part="modal-close-button"
            @click=${() =>
              this.dispatchEvent(
                new CustomEvent("close-user-profile", {
                  bubbles: true,
                  composed: true,
                }),
              )}
          >
            ${closeIcon()}
          </rtg-button>
        </div>
      </div>
    `;
  }

  private renderMobileDrawer() {
    return html`
      <div
        part="mobile-drawer"
        class="${cn(
          MOBILE_DRAWER_CLASSES,
          this.isMenuOpen
            ? "sb-translate-x-0 sb-opacity-100"
            : "sb-translate-x-[-100%] sb-opacity-0",
        )}"
      >
        <div class="sb-account-settings-sidebar-mobile-body">
          <div class="sb-account-settings-sidebar-tab-list">
            <div
              class="sb-absolute sb-right-5 sb-top-4"
              ?hidden=${!this.isBurger}
            >
              <rtg-button
                variant="link"
                size="icon-xs"
                part="burger-close-button"
                @click=${() => this.toggleMenu()}
              >
                ${closeIcon()}
              </rtg-button>
            </div>
            ${this.renderTabs(false)}
          </div>
          ${this.renderWatermark()}
        </div>
      </div>
      <div
        class="${MOBILE_OVERLAY_CLASSES} ${this.isMenuOpen
          ? "sb-block sb-opacity-100"
          : "sb-hidden sb-opacity-0"}"
        @click=${this.toggleMenu}
      ></div>
    `;
  }

  private getSidebarContainerClass() {
    if (this.isBurger && this.isModal) {
      return SIDEBAR_CONTAINER_CLASSES.horizontalBurger;
    }

    if (this.isModal && this.isVertical) {
      return `${SIDEBAR_CONTAINER_CLASSES.vertical} sb-account-settings-sidebar-modal-margin`;
    }

    if (this.isVertical) {
      return SIDEBAR_CONTAINER_CLASSES.vertical;
    }

    return SIDEBAR_CONTAINER_CLASSES.horizontal;
  }

  render() {
    const showHorizontalContent = !this.isVertical && !this.isBurger;

    return html`
      <div
        burger=${this.isBurger}
        class="${cn(
          "sb-overflow-x-auto sb-flex sb-items-center",
          this.getSidebarContainerClass(),
        )}"
      >
        ${this.isBurger ? this.renderBurger() : nothing}
        ${this.isVertical ? this.renderTabs(true) : null}
        ${showHorizontalContent
          ? html`
              <div class="sb-flex sb-flex-col sb-justify-between sb-flex-1">
                ${this.renderTabs(false)} ${this.renderWatermark()}
              </div>
            `
          : null}
      </div>

      ${this.isBurger ? this.renderMobileDrawer() : null}
    `;
  }
}
